import React, { useState, useEffect } from 'react';
import { useNavigate, Outlet } from 'react-router-dom';
import {
  AppShell,
  Text,
  Group,
  Button,
  ThemeIcon,
  Avatar,
  Menu,
  Burger,
  ScrollArea,
  Divider,
  Badge,
  Stack,
  ActionIcon,
  Tooltip,
  Box,
  UnstyledButton
} from '@mantine/core';
import {
  IconHome,
  IconUser,
  IconLogout,
  IconBuilding,
  IconUsers,
  IconBrush,
  IconSettings,
  IconBell,
  IconCalendar,
  IconClipboardList,
  IconTools,
  IconChartBar,
  IconUserCheck,
  IconCreditCard,
  IconHelp
} from '@tabler/icons-react';

function SimpleLayout() {
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userRole, setUserRole] = useState('');
  const [activeMenu, setActiveMenu] = useState('Home');
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    
    setFirstName(localStorage.getItem('userFirstName') || '');
    setLastName(localStorage.getItem('userLastName') || '');
    setUserRole(localStorage.getItem('userRole') || 'user');
  }, [navigate]);

  const handleLogoff = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('userFirstName');
    localStorage.removeItem('userLastName');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  const topMenuItems = [
    { label: 'Home', icon: IconHome, path: '/dashboard', color: 'blue' },
    { label: 'Reservations', icon: IconCalendar, color: 'green', hasDropdown: true },
    { label: 'Front Desk', icon: IconClipboardList, color: 'cyan', hasDropdown: true },
    { label: 'Housekeeping', icon: IconBrush, color: 'orange', hasDropdown: true },
    { label: 'Work Orders', icon: IconTools, color: 'red', hasDropdown: true },
    { label: 'Reports', icon: IconChartBar, color: 'purple', hasDropdown: true },
    { label: 'Owners', icon: IconUserCheck, color: 'teal', hasDropdown: true },
    { label: 'Accounts', icon: IconUsers, color: 'violet', hasDropdown: true },
    { label: 'Accounting', icon: IconCreditCard, color: 'indigo', hasDropdown: true },
    { label: 'Setup', icon: IconSettings, color: 'gray', hasDropdown: true },
    { label: 'Help', icon: IconHelp, color: 'dark', hasDropdown: true }
  ];

  const dropdownItems = {
    Reservations: ['Paying Guest', 'Group', 'Comp', 'Owner', 'Guest of Owner', 'Timeshare/Fractional', 'Timeshare Bonus', 'Maintenance', 'Folio'],
    'Front Desk': ['Find', 'List', 'House Status', 'Arrivals', 'In-House', 'Departures', 'Availability', 'Booking Chart', 'Day End'],
    Housekeeping: ['Clean Status', 'Schedule', 'Forecast', 'History'],
    'Work Orders': ['Find', 'List', 'Add', 'Recurring', 'Setup'],
    Reports: ['Reports', 'Scheduled', 'Automatic'],
    Owners: ['Find', 'List', 'Add', 'Month End', 'Timeshare'],
    Accounts: ['Guests', 'Agents', 'Groups', 'Units', 'Owners'],
    Accounting: ['Account List', 'Trial Balance', 'Ledgers', 'GL Accounts', 'Transactions', 'Taxes'],
    Setup: ['Users & Security', 'Reservations', 'Rates', 'Front Desk', 'Housekeeping', 'Work Orders', 'Reports', 'Owner & Timeshare', 'Booking & Portal', 'Accounts'],
    Help: ['Version', 'Release Notes', 'Log', 'Support']
  };

  const submenuItems = {
    Guests: ['Find', 'List', 'Add'],
    Agents: ['Find', 'List', 'Add'],
    Groups: ['Find', 'List', 'Add'],
    Units: ['Find', 'List', 'Add'],
    Owners: ['Find', 'List', 'Add'],
    'Month End': ['Process Revenue', 'Res Chargers', 'Recurring Charges', 'Interest and Late Fees', 'Import Charges', 'Work Order Charges', 'Statements'],
    'Timeshare': ['Owner Reference', 'Generate Weeks', 'Timeshare Calendar'],
    'Day End': ['Bucket Check', 'Cancel No-Shows', 'Process Deposits', 'Close Day'],
    'Vendors': ['List', 'Add', 'Change', 'Delete'],
    'GL Accounts': ['Find', 'List', 'Add', 'Change', 'Delete', 'Adjust'],
    'Transactions': ['Find', 'List', 'Add', 'Change', 'Delete'],
    'Taxes': ['List', 'Add', 'Change', 'Delete'],
    'Users & Security': ['Users', 'Security'],
    'Rates': ['Rate Management', 'BAR Headers', 'BAR Rates'],
    'Owner & Timeshare': ['Owner', 'Timeshare'],
    'Booking & Portal': ['Booking Engine', 'Owner Portal'],
    'Accounts': ['Guests', 'Agents', 'Groups', 'Units']
  };

  return (
    <AppShell
      header={{ height: 140 }}
      padding="md"
    >
      <AppShell.Header>
        <Stack gap="sm" h="100%" p="md">
          {/* Top row with logo and user menu */}
          <Group justify="space-between">
            <Group gap="sm">
              <ThemeIcon color="blue" variant="light" size="lg">
                <IconBuilding size="1.5rem" />
              </ThemeIcon>
              <Text size="xl" fw={700} c="dark">
                Resort Systems
              </Text>
            </Group>
            
            <Group gap="sm">
              <Tooltip label="Notifications">
                <ActionIcon variant="subtle" color="gray">
                  <IconBell size="1.2rem" />
                </ActionIcon>
              </Tooltip>
              
              <Menu shadow="md" width={200}>
                <Menu.Target>
                  <Button variant="subtle" color="gray">
                    <Group gap="sm">
                      <Avatar color="blue" radius="xl">
                        {firstName.charAt(0)}{lastName.charAt(0)}
                      </Avatar>
                      <Box>
                        <Text size="sm" fw={500}>
                          {firstName} {lastName}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {userRole}
                        </Text>
                      </Box>
                    </Group>
                  </Button>
                </Menu.Target>
                
                <Menu.Dropdown>
                  <Menu.Item leftSection={<IconUser size="1rem" />}>
                    Profile
                  </Menu.Item>
                  <Menu.Item leftSection={<IconSettings size="1rem" />}>
                    Settings
                  </Menu.Item>
                  <Menu.Divider />
                  <Menu.Item 
                    leftSection={<IconLogout size="1rem" />}
                    onClick={handleLogoff}
                    color="red"
                  >
                    Logout
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </Group>
          </Group>

          {/* Navigation menu row */}
          <Group gap="xs">
            {topMenuItems.map((item, index) => {
              const Icon = item.icon;
              
              if (item.hasDropdown) {
                return (
                  <div key={index} style={{ position: 'relative' }}>
                    <Button
                      variant={activeMenu === item.label ? "light" : "subtle"}
                      color={item.color}
                      leftSection={<Icon size="1rem" />}
                      size="sm"
                      onClick={() => {
                        setOpenDropdown(openDropdown === item.label ? null : item.label);
                        setOpenSubmenu(null);
                      }}
                    >
                      {item.label}
                    </Button>
                    
                    {openDropdown === item.label && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e9ecef',
                        borderRadius: '6px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 1000,
                        minWidth: '200px',
                        padding: '8px 0'
                      }}>
                        {dropdownItems[item.label]?.map((dropdownItem, idx) => {
                          // Handle submenus for Accounts
                          if (item.label === 'Accounts' && ['Guests', 'Agents', 'Groups', 'Units', 'Owners'].includes(dropdownItem)) {
                            return (
                              <div key={idx} style={{ position: 'relative' }}>
                                <div
                                  style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    backgroundColor: openSubmenu === dropdownItem ? '#f8f9fa' : 'transparent'
                                  }}
                                  onClick={() => {
                                    setOpenSubmenu(openSubmenu === dropdownItem ? null : dropdownItem);
                                  }}
                                >
                                  <span>{dropdownItem}</span>
                                  <span>▶</span>
                                </div>
                                {openSubmenu === dropdownItem && (
                                  <div style={{
                                    position: 'absolute',
                                    left: '100%',
                                    top: 0,
                                    backgroundColor: 'white',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    minWidth: '120px',
                                    padding: '8px 0'
                                  }}>
                                    {submenuItems[dropdownItem]?.map((subItem, subIdx) => (
                                      <div
                                        key={subIdx}
                                        style={{
                                          padding: '8px 16px',
                                          cursor: 'pointer',
                                          backgroundColor: 'transparent'
                                        }}
                                        onClick={() => {
                                          console.log('Navigating to:', dropdownItem, subItem);
                                          setActiveMenu(item.label);
                                          setOpenDropdown(null);
                                          setOpenSubmenu(null);
                                          // Navigate based on submenu item
                                          if (dropdownItem === 'Guests') {
                                            if (subItem === 'Find') {
                                              console.log('Navigating to guests-find');
                                              navigate('/dashboard/guests-find');
                                            } else if (subItem === 'List') {
                                              console.log('Navigating to guests-list');
                                              navigate('/dashboard/guests-list');
                                            } else if (subItem === 'Add') {
                                              console.log('Navigating to guests-add');
                                              navigate('/dashboard/guests-add');
                                            }
                                          } else if (dropdownItem === 'Agents') {
                                            if (subItem === 'Find') navigate('/dashboard/agents-find');
                                            else if (subItem === 'List') navigate('/dashboard/agents-list');
                                            else if (subItem === 'Add') navigate('/dashboard/agents-add');
                                          } else if (dropdownItem === 'Groups') {
                                            if (subItem === 'Find') navigate('/dashboard/groups-find');
                                            else if (subItem === 'List') navigate('/dashboard/groups-list');
                                            else if (subItem === 'Add') navigate('/dashboard/groups-add');
                                          } else if (dropdownItem === 'Units') {
                                            if (subItem === 'Find') navigate('/dashboard/units-find');
                                            else if (subItem === 'List') navigate('/dashboard/units-list');
                                            else if (subItem === 'Add') navigate('/dashboard/units-add');
                                          } else if (dropdownItem === 'Owners') {
                                            if (subItem === 'Find') navigate('/dashboard/owners-find');
                                            else if (subItem === 'List') navigate('/dashboard/owners-list');
                                            else if (subItem === 'Add') navigate('/dashboard/owners-add');
                                          }
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                      >
                                        {subItem}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          // Handle submenus for Front Desk (Day End)
                          if (item.label === 'Front Desk' && dropdownItem === 'Day End') {
                            return (
                              <div key={idx} style={{ position: 'relative' }}>
                                <div
                                  style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    backgroundColor: openSubmenu === dropdownItem ? '#f8f9fa' : 'transparent'
                                  }}
                                  onClick={() => {
                                    setOpenSubmenu(openSubmenu === dropdownItem ? null : dropdownItem);
                                  }}
                                >
                                  <span>{dropdownItem}</span>
                                  <span>▶</span>
                                </div>
                                {openSubmenu === dropdownItem && (
                                  <div style={{
                                    position: 'absolute',
                                    left: '100%',
                                    top: 0,
                                    backgroundColor: 'white',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    minWidth: '180px',
                                    padding: '8px 0'
                                  }}>
                                    {submenuItems[dropdownItem]?.map((subItem, subIdx) => (
                                      <div
                                        key={subIdx}
                                        style={{
                                          padding: '8px 16px',
                                          cursor: 'pointer',
                                          backgroundColor: 'transparent'
                                        }}
                                        onClick={() => {
                                          console.log('Navigating to:', dropdownItem, subItem);
                                          setActiveMenu(item.label);
                                          setOpenDropdown(null);
                                          setOpenSubmenu(null);
                                          // Handle Day End navigation
                                          if (subItem === 'Bucket Check') {
                                            navigate('/dashboard/day-end-bucket-check');
                                          } else if (subItem === 'Cancel No-Shows') {
                                            navigate('/dashboard/day-end-cancel-no-shows');
                                          } else if (subItem === 'Process Deposits') {
                                            navigate('/dashboard/day-end-process-deposits');
                                          } else if (subItem === 'Close Day') {
                                            navigate('/dashboard/day-end-close-day');
                                          }
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                      >
                                        {subItem}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          
                          // Handle submenus for Accounting (GL Accounts, Transactions, Taxes)
                          if (item.label === 'Accounting' && ['GL Accounts', 'Transactions', 'Taxes'].includes(dropdownItem)) {
                            return (
                              <div key={idx} style={{ position: 'relative' }}>
                                <div
                                  style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    backgroundColor: openSubmenu === dropdownItem ? '#f8f9fa' : 'transparent'
                                  }}
                                  onClick={() => {
                                    setOpenSubmenu(openSubmenu === dropdownItem ? null : dropdownItem);
                                  }}
                                >
                                  <span>{dropdownItem}</span>
                                  <span>▶</span>
                                </div>
                                {openSubmenu === dropdownItem && (
                                  <div style={{
                                    position: 'absolute',
                                    left: '100%',
                                    top: 0,
                                    backgroundColor: 'white',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    minWidth: '120px',
                                    padding: '8px 0'
                                  }}>
                                    {submenuItems[dropdownItem]?.map((subItem, subIdx) => (
                                      <div
                                        key={subIdx}
                                        style={{
                                          padding: '8px 16px',
                                          cursor: 'pointer',
                                          backgroundColor: 'transparent'
                                        }}
                                        onClick={() => {
                                          console.log('Navigating to:', dropdownItem, subItem);
                                          setActiveMenu(item.label);
                                          setOpenDropdown(null);
                                          setOpenSubmenu(null);
                                          // Handle Accounting submenu navigation
                                          if (dropdownItem === 'GL Accounts') {
                                            if (subItem === 'Find') {
                                              navigate('/dashboard/gl-accounts-find');
                                            } else if (subItem === 'List') {
                                              navigate('/dashboard/gl-accounts-list');
                                            } else if (subItem === 'Add') {
                                              navigate('/dashboard/gl-accounts-add');
                                            } else if (subItem === 'Change') {
                                              navigate('/dashboard/gl-accounts-change');
                                            } else if (subItem === 'Delete') {
                                              navigate('/dashboard/gl-accounts-delete');
                                            } else if (subItem === 'Adjust') {
                                              navigate('/dashboard/gl-accounts-adjust');
                                            }
                                          } else if (dropdownItem === 'Transactions') {
                                            if (subItem === 'Find') {
                                              navigate('/dashboard/transactions-find');
                                            } else if (subItem === 'List') {
                                              navigate('/dashboard/transactions-list');
                                            } else if (subItem === 'Add') {
                                              navigate('/dashboard/transactions-add');
                                            } else if (subItem === 'Change') {
                                              navigate('/dashboard/transactions-change');
                                            } else if (subItem === 'Delete') {
                                              navigate('/dashboard/transactions-delete');
                                            }
                                          } else if (dropdownItem === 'Taxes') {
                                            if (subItem === 'List') {
                                              navigate('/dashboard/taxes-list');
                                            } else if (subItem === 'Add') {
                                              navigate('/dashboard/taxes-add');
                                            } else if (subItem === 'Change') {
                                              navigate('/dashboard/taxes-change');
                                            } else if (subItem === 'Delete') {
                                              navigate('/dashboard/taxes-delete');
                                            }
                                          }
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                      >
                                        {subItem}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          // Handle submenus for Setup
                          if (item.label === 'Setup' && ['Users & Security', 'Rates', 'Owner & Timeshare', 'Booking & Portal', 'Accounts'].includes(dropdownItem)) {
                            return (
                              <div key={idx} style={{ position: 'relative' }}>
                                <div
                                  style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    backgroundColor: openSubmenu === dropdownItem ? '#f8f9fa' : 'transparent'
                                  }}
                                  onClick={() => {
                                    setOpenSubmenu(openSubmenu === dropdownItem ? null : dropdownItem);
                                  }}
                                >
                                  <span>{dropdownItem}</span>
                                  <span>▶</span>
                                </div>
                                {openSubmenu === dropdownItem && (
                                  <div style={{
                                    position: 'absolute',
                                    left: '100%',
                                    top: 0,
                                    backgroundColor: 'white',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    minWidth: '150px',
                                    padding: '8px 0'
                                  }}>
                                    {submenuItems[dropdownItem]?.map((subItem, subIdx) => (
                                      <div
                                        key={subIdx}
                                        style={{
                                          padding: '8px 16px',
                                          cursor: 'pointer',
                                          backgroundColor: 'transparent'
                                        }}
                                        onClick={() => {
                                          console.log('Navigating to:', dropdownItem, subItem);
                                          setActiveMenu(item.label);
                                          setOpenDropdown(null);
                                          setOpenSubmenu(null);
                                          // Handle Setup submenu navigation
                                          if (dropdownItem === 'Users & Security') {
                                            if (subItem === 'Users') {
                                              navigate('/dashboard/setup-users');
                                            } else if (subItem === 'Security') {
                                              navigate('/dashboard/setup-security');
                                            }
                                          } else if (dropdownItem === 'Rates') {
                                            if (subItem === 'Rate Management') {
                                              navigate('/dashboard/setup-rates-management');
                                            } else if (subItem === 'BAR Headers') {
                                              navigate('/dashboard/setup-bar-headers');
                                            } else if (subItem === 'BAR Rates') {
                                              navigate('/dashboard/setup-bar-rates');
                                            }
                                          } else if (dropdownItem === 'Owner & Timeshare') {
                                            if (subItem === 'Owner') {
                                              navigate('/dashboard/setup-owner');
                                            } else if (subItem === 'Timeshare') {
                                              navigate('/dashboard/setup-timeshare');
                                            }
                                          } else if (dropdownItem === 'Booking & Portal') {
                                            if (subItem === 'Booking Engine') {
                                              navigate('/dashboard/setup-booking-engine');
                                            } else if (subItem === 'Owner Portal') {
                                              navigate('/dashboard/setup-owner-portal');
                                            }
                                          } else if (dropdownItem === 'Accounts') {
                                            if (subItem === 'Guests') {
                                              navigate('/dashboard/setup-guests');
                                            } else if (subItem === 'Agents') {
                                              navigate('/dashboard/setup-agents');
                                            } else if (subItem === 'Groups') {
                                              navigate('/dashboard/setup-groups');
                                            } else if (subItem === 'Units') {
                                              navigate('/dashboard/setup-units');
                                            }
                                          }
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                      >
                                        {subItem}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          // Handle submenus for Owners (Month End and Timeshare)
                          if (item.label === 'Owners' && ['Month End', 'Timeshare'].includes(dropdownItem)) {
                            return (
                              <div key={idx} style={{ position: 'relative' }}>
                                <div
                                  style={{
                                    padding: '8px 16px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    backgroundColor: openSubmenu === dropdownItem ? '#f8f9fa' : 'transparent'
                                  }}
                                  onClick={() => {
                                    setOpenSubmenu(openSubmenu === dropdownItem ? null : dropdownItem);
                                  }}
                                >
                                  <span>{dropdownItem}</span>
                                  <span>▶</span>
                                </div>
                                {openSubmenu === dropdownItem && (
                                  <div style={{
                                    position: 'absolute',
                                    left: '100%',
                                    top: 0,
                                    backgroundColor: 'white',
                                    border: '1px solid #e9ecef',
                                    borderRadius: '6px',
                                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                    minWidth: '180px',
                                    padding: '8px 0'
                                  }}>
                                    {submenuItems[dropdownItem]?.map((subItem, subIdx) => (
                                      <div
                                        key={subIdx}
                                        style={{
                                          padding: '8px 16px',
                                          cursor: 'pointer',
                                          backgroundColor: 'transparent'
                                        }}
                                        onClick={() => {
                                          console.log('Navigating to:', dropdownItem, subItem);
                                          setActiveMenu(item.label);
                                          setOpenDropdown(null);
                                          setOpenSubmenu(null);
                                          // Navigate based on submenu item
                                          if (dropdownItem === 'Month End') {
                                            // Handle Month End navigation
                                            if (subItem === 'Process Revenue') {
                                              navigate('/dashboard/month-end-process-revenue');
                                            } else if (subItem === 'Res Chargers') {
                                              navigate('/dashboard/month-end-res-chargers');
                                            } else if (subItem === 'Recurring Charges') {
                                              navigate('/dashboard/month-end-recurring-charges');
                                            } else if (subItem === 'Interest and Late Fees') {
                                              navigate('/dashboard/month-end-interest-late-fees');
                                            } else if (subItem === 'Import Charges') {
                                              navigate('/dashboard/month-end-import-charges');
                                            } else if (subItem === 'Work Order Charges') {
                                              navigate('/dashboard/month-end-work-order-charges');
                                            } else if (subItem === 'Statements') {
                                              navigate('/dashboard/month-end-statements');
                                            }
                                          } else if (dropdownItem === 'Timeshare') {
                                            // Handle Timeshare navigation
                                            if (subItem === 'Owner Reference') {
                                              navigate('/dashboard/timeshare-owner-reference');
                                            } else if (subItem === 'Generate Weeks') {
                                              navigate('/dashboard/timeshare-generate-weeks');
                                            } else if (subItem === 'Timeshare Calendar') {
                                              navigate('/dashboard/timeshare-calendar');
                                            }
                                          }
                                        }}
                                        onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                                        onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                      >
                                        {subItem}
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          }
                          
                          return (
                            <div
                              key={idx}
                              style={{
                                padding: '8px 16px',
                                cursor: 'pointer',
                                backgroundColor: 'transparent'
                              }}
                              onClick={() => {
                                setActiveMenu(item.label);
                                setOpenDropdown(null);
                                // Handle specific navigation
                                if (item.label === 'Housekeeping' && dropdownItem === 'Clean Status') {
                                  navigate('/dashboard/housekeeping-clean-status');
                                } else if (item.label === 'Setup' && dropdownItem === 'Units') {
                                  navigate('/dashboard/setup-units');
                                } else if (item.label === 'Setup' && dropdownItem === 'Rates') {
                                  navigate('/dashboard/setup-rates');
                                } else if (item.label === 'Setup' && dropdownItem === 'Housekeeping') {
                                  navigate('/dashboard/setup-housekeeping');
                                } else if (item.label === 'Setup' && dropdownItem === 'Work Orders') {
                                  navigate('/dashboard/setup-work-orders');
                                } else if (item.label === 'Reports') {
                                  // Handle top-level Reports menu navigation
                                  if (dropdownItem === 'Reports') {
                                    navigate('/dashboard/reports');
                                  } else if (dropdownItem === 'Scheduled') {
                                    navigate('/dashboard/reports-scheduled');
                                  } else if (dropdownItem === 'Automatic') {
                                    navigate('/dashboard/reports-automatic');
                                  }
                                } else if (item.label === 'Setup' && dropdownItem === 'Front Desk') {
                                  navigate('/dashboard/setup-front-desk');
                                } else if (item.label === 'Setup' && dropdownItem === 'Reservations') {
                                  navigate('/dashboard/setup-reservations');
                                } else if (item.label === 'Setup' && dropdownItem === 'Reports') {
                                  navigate('/dashboard/setup-reports');
                                } else if (item.label === 'Owners') {
                                  // Handle top-level Owners menu navigation
                                  if (dropdownItem === 'Find') {
                                    navigate('/dashboard/owners-find');
                                  } else if (dropdownItem === 'List') {
                                    navigate('/dashboard/owners-list');
                                  } else if (dropdownItem === 'Add') {
                                    navigate('/dashboard/owners-add');
                                  }
                                } else if (item.label === 'Front Desk') {
                                  // Handle Front Desk navigation
                                  if (dropdownItem === 'Find') {
                                    navigate('/dashboard/front-desk-find');
                                  } else if (dropdownItem === 'List') {
                                    navigate('/dashboard/front-desk-list');
                                  } else if (dropdownItem === 'House Status') {
                                    navigate('/dashboard/front-desk-house-status');
                                  } else if (dropdownItem === 'Arrivals') {
                                    navigate('/dashboard/front-desk-arrivals');
                                  } else if (dropdownItem === 'In-House') {
                                    navigate('/dashboard/front-desk-in-house');
                                  } else if (dropdownItem === 'Departures') {
                                    navigate('/dashboard/front-desk-departures');
                                  } else if (dropdownItem === 'Availability') {
                                    navigate('/dashboard/front-desk-availability');
                                  } else if (dropdownItem === 'Booking Chart') {
                                    navigate('/dashboard/front-desk-booking-chart');
                                  }
                                } else if (item.label === 'Work Orders') {
                                  // Handle Work Orders navigation
                                  if (dropdownItem === 'Find') {
                                    navigate('/dashboard/work-orders-find');
                                  } else if (dropdownItem === 'List') {
                                    navigate('/dashboard/work-orders-list');
                                  } else if (dropdownItem === 'Add') {
                                    navigate('/dashboard/work-orders-add');
                                  } else if (dropdownItem === 'Recurring') {
                                    navigate('/dashboard/work-orders-recurring');
                                  } else if (dropdownItem === 'Setup') {
                                    navigate('/dashboard/setup-work-orders');
                                  }
                                } else if (item.label === 'Reservations') {
                                  // Handle Reservations navigation
                                  if (dropdownItem === 'Paying Guest') {
                                    navigate('/dashboard/reservations-paying-guest');
                                  } else if (dropdownItem === 'Group') {
                                    navigate('/dashboard/reservations-group');
                                  } else if (dropdownItem === 'Comp') {
                                    navigate('/dashboard/reservations-comp');
                                  } else if (dropdownItem === 'Owner') {
                                    navigate('/dashboard/reservations-owner');
                                  } else if (dropdownItem === 'Guest of Owner') {
                                    navigate('/dashboard/reservations-guest-of-owner');
                                  } else if (dropdownItem === 'Timeshare/Fractional') {
                                    navigate('/dashboard/reservations-timeshare');
                                  } else if (dropdownItem === 'Timeshare Bonus') {
                                    navigate('/dashboard/reservations-timeshare-bonus');
                                  } else if (dropdownItem === 'Maintenance') {
                                    navigate('/dashboard/reservations-maintenance');
                                  } else if (dropdownItem === 'Folio') {
                                    navigate('/dashboard/reservations-folio');
                                  }
                                } else if (item.label === 'Accounting') {
                                  // Handle Accounting navigation
                                  if (dropdownItem === 'Account List') {
                                    navigate('/dashboard/accounting-account-list');
                                  } else if (dropdownItem === 'Trial Balance') {
                                    navigate('/dashboard/accounting-trial-balance');
                                  } else if (dropdownItem === 'Ledgers') {
                                    navigate('/dashboard/accounting-ledgers');
                                  }
                                }
                                // Add more navigation logic as needed
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              {dropdownItem}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
              
              return (
                <Button
                  key={index}
                  variant={activeMenu === item.label ? "light" : "subtle"}
                  color={item.color}
                  leftSection={<Icon size="1rem" />}
                  onClick={() => {
                    setActiveMenu(item.label);
                    navigate(item.path);
                  }}
                  size="sm"
                >
                  {item.label}
                </Button>
              );
            })}
          </Group>
        </Stack>
      </AppShell.Header>

      <AppShell.Main>
        <Outlet />
      </AppShell.Main>
    </AppShell>
  );
}

export default SimpleLayout;
